import { createEmptyClip, type ClipIR, type NoteFx, type PatternCell } from "./types";

/**
 * Celeste Classic (PICO-8) — Matt Thorson + Noel Berry cart music, decoded
 * from test-docs/celeste.p8 `__sfx__` / `__music__` and rendered as a one-shot
 * tracker medley.
 *
 * ## Songs (music() indices in the cart)
 * Title (40) → Prologue (0) → Old Site (10) → Resort (20) → Summit (30).
 * Each song plays its loop range once through (loop-start..loop-end inclusive).
 *
 * ## Grid
 * One PICO-8 tick = 1/128 s. Tracker step = 4 ticks → 1/32 s.
 * bpm=120, stepDivision=64 → secondsPerStep = 1/32. A note of SFX speed S
 * occupies S/4 steps. Pattern length = max over channels of 32·speed ticks.
 * Shorter channels loop their SFX loop range to fill the pattern.
 *
 * ## Tracks
 * PICO-8 has four channels; we split each into melodic vs noise (wave 6) so
 * percussion keeps a noise voice while pitched lines keep a chip lead.
 */

const BPM = 120;
const STEP_DIVISION = 64;
const TICKS_PER_STEP = 4;
const MIDI_OFFSET = 36; // PICO C-0 → MIDI C2

export const CELESTE_TRACK_NAMES = [
  "Ch1 Lead",
  "Ch2 Lead",
  "Ch3 Lead",
  "Ch4 Lead",
  "Ch1 Noise",
  "Ch2 Noise",
  "Ch3 Noise",
  "Ch4 Noise",
] as const;

export const CELESTE_TRACK_PRESETS = [
  "Iceberg Lead",
  "Chirp Arp",
  "Tracker Choir",
  "Glass Bell",
  "Tick Hat",
  "Paper Snare",
  "Tick Hat",
  "Paper Snare",
] as const;

/** Embedded from celeste.p8 — do not hand-edit. */
const SFX_HEX = "0002000036370234702f3701d4702a37017470273701347023370114701e3700e4701a3600c46016350084401233005420196001960019600196003f6003f6003f6003f6003f6003f6003f6003f6003f6003f600\n0002000011070130701a0702407000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000\n000300000d07010070160702207000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000\n000200000642008420094200b420224402a4503c6503b6503b6503965036650326502d6502865024640216401d6401a64016630116300e6300b62007620056100361010600106000060000600006000060000600\n000400000f0701e070120702207017070260701b0602c060210503105027040360402b0303a030300203e02035010000000000000000000000000000000000000000000000000000000000000000000000000000\n000300000977009770097600975008740077300672005715357003470034700347003470034700347003570035700357003570035700347003470034700337003370033700337000070000700007000070000700\n00030000241700e1702d1701617034170201603b160281503f1402f120281101d1101011003110001000010000100001000010000100001000010000100001000010000100001000010000100001000010000100\n00020000101101211014110161101a120201202613032140321403410000100001000010000100001000010000100001000010000100001000010000100001000010000100001000010000100001000010000100\n00030000070700a0700e0701007016070220702f0702f0602c0602c0502f0502f0402c0402c0302f0202f0102c000000000000000000000000000000000000000000000000000000000000000000000000000000\n0003000005110071303f6403f6403f6303f6203f6103f6153f6003f6003f600006000060000600006000060000600006000060000600006000060000600006000060000600006000060000600006000060000600\n011000200177500605017750170523655017750160500605017750060501705076052365500605017750060501775017050177500605236550177501605006050177500605256050160523655256050177523655\n002000001d0401d0401d0301d020180401804018030180201b0301b02022040220461f0351f03016040160401d0401d0401d002130611803018030180021f061240502202016040130201d0401b0221804018040\n00100000070700706007050110000707007060030510f0700a0700a0600a0500a0000a0700a0600505005040030700306003000030500c0700c0601105016070160600f071050500a07005050030510a0700a060\n000400000c5501c5601057023570195702c5702157037570285703b5702c5703e560315503e540315303e530315203f520315203f520315103f510315103f510315103f510315103f50000500005000050000500\n000400002f7402b760267701d7701577015770197701c750177300170015700007000070000700007000070000700007000070000700007000070000700007000070000700007000070000700007000070000700\n00030000096450e655066550a6550d6550565511655076550c655046550965511645086350d615006050060500605006050060500605006050060500605006050060500605006050060500605006050060500605\n011000001f37518375273752730027300243001d300263002a3001c30019300003000030000300003000030000300003000030000300003000030000300003000030000300003000030000300003000030000300\n011000002953429554295741d540225702256018570185701856018500185701856000500165701657216562275142753427554275741f5701f5601f500135201b55135530305602454029570295602257022560\n011000200a0700a0500f0710f0500a0600a040110701105007000070001107011050070600704000000000000a0700a0500f0700f0500a0600a0401307113050000000000013070130500f0700f0500000000000\n002000002204022030220201b0112404024030270501f0202b0402202027050220202904029030290201601022040220302b0401b030240422403227040180301d0401d0301f0521f0421f0301d0211d0401d030\n0108002001770017753f6253b6003c6003b6003f6253160023650236553c600000003f62500000017750170001770017753f6003f6003f625000003f62500000236502365500000000003f625000000000000000\n002000200a1400a1300a1201113011120111101b1401b13018152181421813213140131401313013120131100f1400f1300f12011130111201111016142161321315013140131301312013110131101311013100\n001000202e750377502e730377302e720377202e71037710227502b750227302b7301d750247501d730247301f750277501f730277301f7202772029750307502973030730297203072029710307102971030710\n000600001877035770357703576035750357403573035720357103570000700007000070000700007000070000700007000070000700007000070000700007000070000700007000070000700007000070000700\n001800202945035710294403571029430377102942037710224503571022440274503c710274403c710274202e450357102e440357102e430377102e420377102e410244402b45035710294503c710294403c710\n0018002005570055700557005570055700000005570075700a5700a5700a570000000a570000000a5700357005570055700557000000055700557005570000000a570075700c5700c5700f570000000a57007570\n010c00103b6352e6003b625000003b61500000000003360033640336303362033610336103f6003f6150000000000000000000000000000000000000000000000000000000000000000000000000000000000000\n000c002024450307102b4503071024440307002b44037700244203a7102b4203a71024410357102b410357101d45033710244503c7101d4403771024440337001d42035700244202e7101d4102e7102441037700\n011800200c5700c5600c550000001157011560115500c5000c5700c5600f5710f56013570135600a5700a5600c5700c5600c550000000f5700f5600f550000000a5700a5600a5500f50011570115600a5700a560\n001800200c5700c5600c55000000115701156011550000000c5700c5600f5710f56013570135600f5700f5600c5700c5700c5600c5600c5500c5300c5000c5000c5000a5000a5000a50011500115000a5000a500\n000c0020247712477024762247523a0103a010187523a0103501035010187523501018750370003700037000227712277222762227001f7711f7721f762247002277122772227620070027771277722776200700\n000c0020247712477024762247523a0103a010187503a01035010350101875035010187501870018700007001f7711f7701f7621f7521870000700187511b7002277122770227622275237012370123701237002\n000c0000247712477024772247722476224752247422473224722247120070000700007000070000700007002e0002e0002e0102e010350103501033011330102b0102b0102b0102b00030010300123001230012\n000c00200c3320c3320c3220c3220c3120c3120c3120c3020c3320c3320c3220c3220c3120c3120c3120c30207332073320732207322073120731207312073020a3320a3320a3220a3220a3120a3120a3120a302\n000c00000c3300c3300c3200c3200c3100c3100c3103a0000c3300c3300c3200c3200c3100c3100c3103f0000a3300a3201333013320073300732007310113000a3300a3200a3103c0000f3300f3200f3103a000\n00040000336251a605000050000500005000050000500005000050000500005000050000500005000050000500005000050000500005000050000500005000050000500005000050000500005000050000500005\n000c00000c3300c3300c3300c3200c3200c3200c3100c3100c3100c31000000000000000000000000000000000000000000000000000000000000000000000000a3000a3000a3000a3000a3310a3300332103320\n001000000c3500c3400c3300c3200f3500f3400f3300f320183501834013350133401835013350163401d36022370223702236022350223402232013300133001830018300133001330016300163001d3001d300\n000c0000242752b27530275242652b26530265242552b25530255242452b24530245242352b23530235242252b22530225242152b21530215242052b20530205242052b205302053a2052e205002050020500205\n001000102f65501075010753f615010753f6152f65501075010753f615010753f6152f6553f615010753f61500005000050000500005000050000500005000050000500005000050000500005000050000500005\n0010000016270162701f2711f2701f2701f270182711827013271132701d2711d270162711627016270162701b2711b2701b2701b270000001b200000001b2000000000000000000000000000000000000000000\n00080020245753057524545305451b565275651f5752b5751f5452b5451f5352b5351f5252b5251f5152b5151b575275751b545275451b535275351d575295751d545295451d535295351f5752b5751f5452b545\n002000200c2650c2650c2550c2550c2450c2450c2350a2310f2650f2650f2550f2550f2450f2450f2351623113265132651325513255132451324513235132351322507240162701326113250132420f2600f250\n00100000072750726507255072450f2650f2550c2750c2650c2550c2450c2350c22507275072650725507245072750726507255072450c2650c25511275112651125511245132651325516275162651625516245\n000800201f5702b5701f5402b54018550245501b570275701b540275401857024570185402454018530245301b570275701b540275401d530295301d520295201f5702b5701f5402b5401f5302b5301b55027550\n00100020112751126511255112451326513255182751826518255182451d2651d2550f2651824513275162550f2750f2650f2550f2451126511255162751626516255162451b2651b255222751f2451826513235\n00100010010752f655010753f6152f6553f615010753f615010753f6152f655010752f6553f615010753f61500005000050000500005000050000500005000050000500005000050000500005000050000500005\n001000100107501075010753f6152f6553f6153f61501075010753f615010753f6152f6553f6152f6553f61500005000050000500005000050000500005000050000500005000050000500005000050000500005\n002000002904029040290302b031290242b021290142b01133044300412e0442e03030044300302b0412b0302e0442e0402e030300312e024300212e024300212b0442e0412b0342e0212b0442b0402903129022\n000800202451524515245252452524535245352454524545245552455524565245652457500505245750050524565005052456500505245550050524555005052454500505245350050524525005052451500505\n000800201f5151f5151f5251f5251f5351f5351f5451f5451f5551f5551f5651f5651f575000051f575000051f565000051f565000051f555000051f555000051f545000051f535000051f525000051f51500005\n000500000373005731077410c741137511b7612437030371275702e5712437030371275702e5712436030361275602e5612435030351275502e5512434030341275402e5412433030331275202e5212431030311\n002000200c2750c2650c2550c2450c2350a2650a2550a2450f2750f2650f2550f2450f2350c2650c2550c2450c2750c2650c2550c2450c2350a2650a2550a2450f2750f2650f2550f2450f235112651125511245\n002000001327513265132551324513235112651125511245162751626516255162451623513265132551324513275132651325513245132350f2650f2550f2450c25011231162650f24516272162520c2700c255\n000300001f3302b33022530295301f3202b32022520295201f3102b31022510295101f3002b300225002950000000000000000000000000000000000000000000000000000000000000000000000000000000000\n000b00002935500300293453037030360303551330524300243050030013305243002430500300003002430024305003000030000300003000030000300003000030000300003000030000300003000030000300\n001000003c5753c5453c5353c5253c5153c51537555375453a5753a5553a5453a5353a5253a5253a5153a51535575355553554535545355353553535525355253551535515335753355533545335353352533515\n00100000355753555535545355353552535525355153551537555375353357533555335453353533525335253a5753a5453a5353a5253a5153a51533575335553354533545335353353533525335253351533515\n001000200c0600c0300c0500c0300c0500c0300c0100c0000c0600c0300c0500c0300c0500c0300c0100f0001106011030110501103011010110000a0600a0300a0500a0300a0500a0300a0500a0300a01000000\n001000000506005030050500503005010050000706007030070500703007010000000f0600f0300f010000000c0600c0300c0500c0300c0500c0300c0500c0300c0500c0300c010000000c0600c0300c0100c000\n0010000003625246150060503615246251b61522625036150060503615116253361522625006051d6250a61537625186152e6251d615006053761537625186152e6251d61511625036150060503615246251d615\n00100020326103261032610326103161031610306102e6102a610256101b610136100f6100d6100c6100c6100c6100c6100c6100f610146101d610246102a6102e61030610316103361033610346103461034610\n00400000302453020530235332252b23530205302253020530205302253020530205302153020530205302152b2452b2052b23527225292352b2052b2252b2052b2052b2252b2052b2052b2152b2052b2052b215";
const MUSIC_HEX = "01 150a5644\n00 0a160c44\n00 0a160c44\n00 0a0b0c44\n00 14131244\n00 0a160c44\n00 0a160c44\n02 0a111244\n00 41424344\n00 41424344\n01 18191a44\n00 18191a44\n00 1c1b1a44\n00 1d1b1a44\n00 1f211a44\n00 1f1a2144\n00 1e1a2244\n02 201a2444\n00 41424344\n00 41424344\n01 2a272944\n00 2a272944\n00 2f2b2944\n00 2f2b2c44\n00 2f2b2944\n00 2f2b2c44\n00 2e2d3044\n00 34312744\n02 35322744\n00 41424344\n01 3d7e4344\n00 3d7e4344\n00 3d4a4344\n02 3d3e4344\n00 41424344\n00 41424344\n00 41424344\n00 41424344\n00 41424344\n00 41424344\n01 383a3c44\n02 393b3c44";

type PicoNote = { pitch: number; wave: number; vol: number; effect: number };
type PicoSfx = { speed: number; loopStart: number; loopEnd: number; notes: PicoNote[] };
type PicoPattern = { flags: number; channels: (number | null)[] };

type SongSpec = { name: string; markerColor: number; from: number; to: number };

const SONGS: readonly SongSpec[] = [
  { name: "Title", markerColor: 0, from: 40, to: 41 },
  { name: "Prologue", markerColor: 1, from: 0, to: 7 },
  { name: "Old Site", markerColor: 2, from: 10, to: 17 },
  { name: "Resort", markerColor: 3, from: 20, to: 28 },
  { name: "Summit", markerColor: 4, from: 30, to: 33 },
];

function parseSfxBlock(block: string): PicoSfx[] {
  const lines = block.split(/\r?\n/).filter(Boolean);
  const out: PicoSfx[] = Array.from({ length: 64 }, () => ({
    speed: 16,
    loopStart: 0,
    loopEnd: 0,
    notes: Array.from({ length: 32 }, () => ({ pitch: 0, wave: 0, vol: 0, effect: 0 })),
  }));
  for (let i = 0; i < Math.min(64, lines.length); i++) {
    const line = lines[i]!;
    if (line.length < 8 + 32 * 5) continue;
    const notes: PicoNote[] = [];
    for (let n = 0; n < 32; n++) {
      const o = 8 + n * 5;
      notes.push({
        pitch: parseInt(line.slice(o, o + 2), 16),
        wave: parseInt(line.slice(o + 2, o + 3), 16),
        vol: parseInt(line.slice(o + 3, o + 4), 16),
        effect: parseInt(line.slice(o + 4, o + 5), 16),
      });
    }
    out[i] = {
      speed: parseInt(line.slice(2, 4), 16),
      loopStart: parseInt(line.slice(4, 6), 16),
      loopEnd: parseInt(line.slice(6, 8), 16),
      notes,
    };
  }
  return out;
}

function parseMusicBlock(block: string): PicoPattern[] {
  return block.split(/\r?\n/).filter(Boolean).map(line => {
    const [flagsHex, rest] = line.trim().split(/\s+/);
    const flags = parseInt(flagsHex ?? "0", 16);
    const channels: (number | null)[] = [];
    for (let i = 0; i < 4; i++) {
      const b = parseInt((rest ?? "").slice(i * 2, i * 2 + 2) || "44", 16);
      channels.push(b & 0x40 ? null : b & 0x3f);
    }
    return { flags, channels };
  });
}

function effectFx(effect: number): NoteFx[] {
  switch (effect) {
    case 1:
      return [{ kind: "portamento", value: 0x48 }];
    case 2:
      return [{ kind: "vibrato", value: 0x68 }];
    case 3:
      return [{ kind: "slideDown", value: 0x40 }];
    default:
      return [];
  }
}

function putNote(
  cells: PatternCell[],
  step: number,
  pitch: number,
  vol: number,
  effect: number,
  gateSteps: number,
): void {
  if (step < 0 || step >= cells.length || vol <= 0) return;
  const fx: NoteFx[] = [...effectFx(effect)];
  const gate = Math.max(1, Math.min(16, Math.round(gateSteps)));
  fx.push({ kind: "cut", value: Math.min(255, Math.round(gate * 16)) });
  cells[step] = {
    note: pitch + MIDI_OFFSET,
    volume: vol / 7,
    fx,
  };
}

function expandChannelNotes(
  sfx: PicoSfx,
  patternTicks: number,
): Array<{ tick: number; note: PicoNote; durTicks: number }> {
  const events: Array<{ tick: number; note: PicoNote; durTicks: number }> = [];
  const speed = Math.max(1, sfx.speed);
  const hasLoop = sfx.loopEnd > sfx.loopStart;
  let tick = 0;
  let index = 0;
  let played = 0;
  while (tick < patternTicks) {
    const note = sfx.notes[index] ?? { pitch: 0, wave: 0, vol: 0, effect: 0 };
    const dur = Math.min(speed, patternTicks - tick);
    if (note.vol > 0) {
      events.push({ tick, note, durTicks: dur });
    }
    tick += speed;
    played += 1;
    index += 1;
    if (index >= 32) {
      if (hasLoop && tick < patternTicks) {
        index = sfx.loopStart;
      } else {
        break;
      }
    }
    if (played > 32 * 8) break;
  }
  return events;
}

function measureCelesteSteps(): number {
  const sfxBank = parseSfxBlock(SFX_HEX);
  const music = parseMusicBlock(MUSIC_HEX);
  let totalTicks = 0;
  for (const song of SONGS) {
    for (let p = song.from; p <= song.to; p++) {
      const pat = music[p];
      if (!pat) continue;
      let maxTicks = 0;
      for (const ch of pat.channels) {
        if (ch == null) continue;
        maxTicks = Math.max(maxTicks, 32 * Math.max(1, sfxBank[ch]!.speed));
      }
      totalTicks += maxTicks || 32 * 16;
    }
  }
  return Math.ceil(totalTicks / TICKS_PER_STEP);
}

export const CELESTE_TOTAL_STEPS = measureCelesteSteps();

export function createCelesteDemoClip(): ClipIR {
  const sfxBank = parseSfxBlock(SFX_HEX);
  const music = parseMusicBlock(MUSIC_HEX);
  const totalSteps = measureCelesteSteps();

  const clip = createEmptyClip({ bpm: BPM, steps: totalSteps, stepDivision: STEP_DIVISION });
  for (let i = 0; i < 8; i++) {
    clip.patterns[0]!.tracks[i]!.name = CELESTE_TRACK_NAMES[i];
  }

  let cursorTicks = 0;
  const pattern = clip.patterns[0]!;

  for (const song of SONGS) {
    const startStep = Math.round(cursorTicks / TICKS_PER_STEP);
    if (!pattern.rowMarkers) pattern.rowMarkers = {};
    pattern.rowMarkers[String(startStep)] = { label: song.name, color: song.markerColor };

    for (let p = song.from; p <= song.to; p++) {
      const pat = music[p];
      if (!pat) continue;
      let patternTicks = 0;
      for (const ch of pat.channels) {
        if (ch == null) continue;
        patternTicks = Math.max(patternTicks, 32 * Math.max(1, sfxBank[ch]!.speed));
      }
      if (patternTicks === 0) patternTicks = 32 * 16;

      for (let ch = 0; ch < 4; ch++) {
        const sfxId = pat.channels[ch];
        if (sfxId == null) continue;
        const sfx = sfxBank[sfxId]!;
        const events = expandChannelNotes(sfx, patternTicks);
        const melodic = pattern.tracks[ch]!.cells;
        const noise = pattern.tracks[ch + 4]!.cells;
        for (const ev of events) {
          const step = Math.round((cursorTicks + ev.tick) / TICKS_PER_STEP);
          const gateSteps = ev.durTicks / TICKS_PER_STEP;
          const dest = ev.note.wave === 6 ? noise : melodic;
          putNote(dest, step, ev.note.pitch, ev.note.vol, ev.note.effect, gateSteps);
        }
      }
      cursorTicks += patternTicks;
    }
  }

  return clip;
}
